import { createApi } from "@reduxjs/toolkit/query/react";
import { customBaseQuery } from "helpers/jwt-token-access/customBaseQuery";

const convertToFormData = (
  obj: Record<string, any> | undefined | null,
  parentKey: string = "",
): FormData => {
  const formData = new FormData();

  const appendData = (key: string, value: any) => {
    if (value instanceof File) {
      formData.append(key, value);
    } else if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (item instanceof File) {
          formData.append(`${key}[${index}]`, item);
        } else {
          formData.append(`${key}[${index}]`, item);
        }
      });
    } else if (value && typeof value === "object") {
      if (value instanceof Date) {
        formData.append(key, value.toISOString());
      } else {
        Object.keys(value).forEach((subKey) => {
          appendData(`${key}[${subKey}]`, value[subKey]);
        });
      }
    } else {
      formData.append(key, value);
    }
  };

  if (obj) {
    Object.keys(obj).forEach((key) => {
      const value = obj[key];
      appendData(parentKey ? `${parentKey}[${key}]` : key, value);
    });
  }

  return formData;
};

export const riskAssessmentSlice = createApi({
  reducerPath: "riskAssement",
  baseQuery: customBaseQuery,
  tagTypes: [
    "GetRiskAssessmentReport",
    "GetRiskAssessmentReportByNumber",
    "GetGeneralworkPermit",
    "GetGeneralworkPermitByNumber",
  ],
  endpoints: (builder) => ({
    //---HomeWork Permit EndPoints
    
    generateRiskAssessmentPDF: builder.mutation({
      query: (assessment_id ) => ({
        url: `/risk_assessment/api/v1/generate-risk-assessment-checklist-pdf/${assessment_id}`,
        method: "GET",
        responseHandler: async (response: any) => {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `${assessment_id}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
        },
      }),
    }),
   

    // --- Hotwork Endpoints ---
    getRiskAssessmentView: builder.query({
      query: (page) =>
        `/risk_assessment/api/v1/risk_assesment_view/?page=${page.page}&page_size=${page.pageSize}`,
      providesTags: ["GetRiskAssessmentReport"],
    }),
    getRiskAssessmentByNumber: builder.query({
      query: (assessment_id ) =>
        `/risk_assessment/api/v1/risk_assesment_view/${assessment_id}/`,
      providesTags: (result, error, assessment_id ) => [
        { type: "GetRiskAssessmentReportByNumber", id: assessment_id  },
      ],
    }),
    createRiskAssessment: builder.mutation({
      query: (newPermitData) => ({
        url: "/risk_assessment/api/v1/risk_assessment_create_update/",
        method: "POST",
        body: newPermitData,
      }),
      invalidatesTags: ["GetRiskAssessmentReport"],
    }),
    // deleteHotworkPermit: builder.mutation({
    //   query: ({ permitNo, permitData }) => ({
    //     url: `/safety_app/api/v1/hotwork_permit_create_update/${permitNo}/?user_comments=${permitData.user_comments}&latitude=${permitData.latitude}&longitude=${permitData.longitude}`,
    //     method: "DELETE",
    //   }),
    // }),
    updateRiskAssessmentPermit: builder.mutation({
      query: ({ assessment_id , updatedPermitData }) => ({
        url: `/risk_assessment/api/v1/risk_assessment_create_update/${assessment_id}/`,
        method: "PATCH",
        body: updatedPermitData,
      }),
      invalidatesTags: (result, error, { assessment_id  }) => [
        { type: "GetRiskAssessmentReportByNumber", id: assessment_id  },
        { type: "GetRiskAssessmentReport" },
      ],
    }),
    getWorkActivityReport: builder.query({
        query: () => "/risk_assessment/api/v1/work_activity/",
    }),
   
    getCheckListQuestionnaireKeys: builder.query({
      query: () => "/risk_assessment/api/v1/risk_assessment_checklist_questionnaire_key/",
    }),
    getCheckListQuestionnaireKeyById: builder.query({
      query: (id) =>
        `/risk_assessment/api/v1/risk_assessment_checklist_questionnaire_key/${id}/`,
    }),
    createCheckListQuestionnaireKey: builder.mutation({
      query: (newQuestionnaireKeyData) => ({
        url: "/risk_assessment/api/v1/risk_assessment_checklist_questionnaire_key/",
        method: "POST",
        body: newQuestionnaireKeyData,
      }),
    }),
    updateCheckListQuestionnaireKeyById: builder.mutation({
      query: ({ id, updatedQuestionnaireKeyData }) => ({
        url: `/risk_assessment/api/v1/risk_assessment_checklist_questionnaire_key/${id}/`,
        method: "PATCH",
        body: updatedQuestionnaireKeyData,
      }),
    }),
    updateCheckListQuestionnaireByPermitNo: builder.mutation({
      query: ({ assessment_id , updatedQuestionnaireKeyData }) => ({
        url: `/risk_assessment/api/v1/risk_assessment_checklist_questionnaire_update/${assessment_id}/`,
        method: "PATCH",
        body: updatedQuestionnaireKeyData,
      }),
      // invalidatesTags: (result, error, { assessment_id  }) => [
      //   { type: "GetRiskAssessmentReportByNumber", id: assessment_id  },
      // ],
    }),

    createRiskMigration: builder.mutation({
        query: ({assessment_id, mitigationReport}) => ({
          url: `/risk_assessment/api/v1/risk_migration_create/${assessment_id}/`,
          method: "POST",
          body: mitigationReport,
        }),
        invalidatesTags: ["GetRiskAssessmentReport"],
      }),

      updateRiskMigration: builder.mutation({
        query: ({id , reportData }) => ({
          url: `/risk_assessment/api/v1/risk_migration_update/${id}/`,
          method: "PATCH",
          body: reportData,
        }),
        // invalidatesTags: (result, error, { assessment_id  }) => [
        //   { type: "GetRiskAssessmentReportByNumber", id: assessment_id  },
        //   { type: "GetRiskAssessmentReport" },
        // ],
      }),

      //....End shift Questionnaire

      getEndShiftQuestionnaireKeys: builder.query({
        query: () => "/risk_assessment/api/v1/risk_assessment_end_of_shift_questionnaire_key/",
      }),
      getEndShiftQuestionnaireKeyById: builder.query({
        query: (id) =>
          `/risk_assessment/api/v1/risk_assessment_end_of_shift_questionnaire_key/${id}/`,
      }),
      createEndShiftQuestionnaireKey: builder.mutation({
        query: (newQuestionnaireKeyData) => ({
          url: "/risk_assessment/api/v1/risk_assessment_end_of_shift_questionnaire_key/",
          method: "POST",
          body: newQuestionnaireKeyData,
        }),
      }),
      updateEndShiftQuestionnaireKeyById: builder.mutation({
        query: ({ id, updatedQuestionnaireKeyData }) => ({
          url: `/risk_assessment/api/v1/risk_assessment_end_of_shift_questionnaire_key/${id}/`,
          method: "PATCH",
          body: updatedQuestionnaireKeyData,
        }),
      }),
      updateEndShiftQuestionnaireByPermitNo: builder.mutation({
        query: ({ assessment_id , updatedQuestionnaireKeyData }) => ({
          url: `/risk_assessment/api/v1/risk_assessment_end_of_shift_questionnaire_update/${assessment_id}/`,
          method: "PATCH",
          body: updatedQuestionnaireKeyData,
        }),
        // invalidatesTags: (result, error, { assessment_id  }) => [
        //   { type: "GetRiskAssessmentReportByNumber", id: assessment_id  },
        // ],
      }),


      createRiskObservation: builder.mutation({
        query: ({assessment_id, observationData}) => ({
          url: `/risk_assessment/api/v1/risk_observation_create/${assessment_id}`,
          method: "POST",
          body: observationData,
        }),
        invalidatesTags: ["GetRiskAssessmentReport"],
      }),

      updateRiskObservation: builder.mutation({
        query: ({id , observationData }) => ({
          url: `/risk_assessment/api/v1/risk_observation_update/${id}/`,
          method: "PATCH",
          body: observationData,
        }),
        // invalidatesTags: (result, error, { assessment_id  }) => [
        //   { type: "GetRiskAssessmentReportByNumber", id: assessment_id  },
        //   { type: "GetRiskAssessmentReport" },
        // ],
      }),
      checkRiskAssessmentStatus: builder.mutation({
        query: ({ project }) => ({
          url: `/risk_assessment/api/v1/get_risk_assessment_verify/?project_id=${project}`,
          method: "GET",
          responseHandler: async (response: Response) => {
            const data = await response.json();
            return data;
          },
        }),
      }),
      // getRiskAssessmentVerifyById: builder.query({
      //   query: (project) =>
      //     `/risk_assessment/api/v1/get_risk_assessment_verify/?project_id=${project}`,
      // }),

       getRiskAssessmentDropDown: builder.query({
        query: ({ project, start_date, end_date }) =>
          `/risk_assessment/api/v1/risk_assessment_drop_down/?project=${project}&start_date=${start_date}&end_date=${end_date}`,
      }),

      updateLinkRiskAssessment: builder.mutation({
        query: ({assessment_id,reportData}) => ({
          url: `/risk_assessment/api/v1/link_risk_assessment/${assessment_id}/`,
          method: "PATCH",
          body: reportData,
        }),
      }),

  }),
});

export const {
 
  useGenerateRiskAssessmentPDFMutation,
 

  //Hot Work Permit
  useGetRiskAssessmentViewQuery,
  useGetRiskAssessmentByNumberQuery,
  useCreateRiskAssessmentMutation,
//   useDeleteHotworkPermitMutation,
  useUpdateRiskAssessmentPermitMutation,
  useCreateRiskMigrationMutation,
  useUpdateRiskMigrationMutation,

 useGetWorkActivityReportQuery,
 useCreateCheckListQuestionnaireKeyMutation,
 useGetCheckListQuestionnaireKeyByIdQuery,
 useGetCheckListQuestionnaireKeysQuery,
 useUpdateCheckListQuestionnaireByPermitNoMutation,
 useUpdateCheckListQuestionnaireKeyByIdMutation,
 
 useCreateEndShiftQuestionnaireKeyMutation,
 useGetEndShiftQuestionnaireKeyByIdQuery,
 useGetEndShiftQuestionnaireKeysQuery,
 useUpdateEndShiftQuestionnaireByPermitNoMutation,
 useUpdateEndShiftQuestionnaireKeyByIdMutation,
 useUpdateRiskObservationMutation,
 useCreateRiskObservationMutation,
 useCheckRiskAssessmentStatusMutation,
//  useGetRiskAssessmentVerifyByIdQuery
useGetRiskAssessmentDropDownQuery,
useUpdateLinkRiskAssessmentMutation, 
} = riskAssessmentSlice;
